import {
  Box,
  Flex,
  FlexProps,
  Icon,
  ListItem,
  Text,
  Tooltip,
  UnorderedList,
} from "@chakra-ui/react";
import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import React from "react";
import { RiErrorWarningLine } from "react-icons/ri";
import { PERMISSION_LEVELS } from "src/constants";
import { DEFAULT_DATE_TYPE_FORMAT } from "src/constants/date";
import { useTimezoneName } from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { FormAddressSchema } from "src/schemas/Address";
import { CustomQuestionAnswerSchema } from "src/schemas/CustomQuestionType";
import {
  formatNumberWithConstraints,
  formatUSPhoneNumber,
} from "src/services/format";
import { Answer } from "src/services/formTemplate";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import { HasuraRole } from "src/types/hasuraRole";
import { InfoBadge } from "../DataDisplay/InfoBadge";
import { QuestionFileItem } from "../UploadButtons/UploadFileQuestion/QuestionFileItem";
import { CustomQuestionAnswer } from "./CustomQuestionAnswer";

type Props = {
  question: AF.Question<AF.WithId>;
  answer: Answer.FormikFieldValue;
  formId: uuid;
} & FlexProps;

export const ParentQuestionAnswer: React.FC<Props> = ({
  question,
  answer,
  formId,
  ...props
}) => {
  return (
    <QuestionAnswer
      question={question}
      answer={answer}
      formId={formId}
      questionColor="gray.700"
      questionFontWeight="500"
      answerColor="blackAlpha.700"
      answerFontWeight="400"
      {...props}
    />
  );
};

export const AdminQuestionAnswer: React.FC<Props> = ({
  question,
  answer,
  formId,
  ...props
}) => {
  return (
    <QuestionAnswer
      question={question}
      answer={answer}
      formId={formId}
      questionColor="gray.600"
      questionFontWeight="500"
      answerColor="gray.800"
      answerFontWeight="400"
      {...props}
    />
  );
};

type QuestionAnswerProps = {
  questionColor?: string;
  questionFontWeight?: string;
  answerColor?: string;
  answerFontWeight?: string;
} & Props;

export const QuestionAnswer: React.FC<QuestionAnswerProps> = ({
  question,
  answer,
  formId,
  questionColor,
  questionFontWeight,
  answerColor,
  answerFontWeight,
  ...props
}) => {
  const timezoneName = useTimezoneName();
  const isAdmin = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.DISTRICT_ADMIN,
    HasuraRole.ORG_ADMIN,
    HasuraRole.SCHOOL_ADMIN,
  ]);

  let children: React.ReactNode;
  switch (question.type) {
    case AF.NumberType: {
      let displayValue = answer?.toString() ?? "";
      let formatError: string | undefined;
      try {
        displayValue = formatNumberWithConstraints(
          answer,
          question.constraints
        );
      } catch (error) {
        console.error(error);
        formatError = INVALID_NUMBER_MESSAGE;
      }
      children = (
        <AnswerWithValidationWarning errorMessage={formatError}>
          <Text>{displayValue}</Text>
        </AnswerWithValidationWarning>
      );
      break;
    }
    case AF.DateType: {
      const stringValue = answer?.toString();
      if (!stringValue) {
        children = <Text></Text>;
        break;
      }
      const parsedDate = parseISO(stringValue);
      const isDateValid = isValid(parsedDate);
      children = (
        <AnswerWithValidationWarning
          errorMessage={isDateValid ? undefined : INVALID_DATE_MESSAGE}
        >
          <Text>
            {isDateValid
              ? formatInTimeZone(
                  stringValue,
                  timezoneName,
                  question.constraints?.format || DEFAULT_DATE_TYPE_FORMAT
                )
              : stringValue}
          </Text>
        </AnswerWithValidationWarning>
      );
      break;
    }
    case AF.PhoneNumberType:
      children = (
        <Text color={answerColor} fontWeight={answerFontWeight}>
          {answer && typeof answer === "string"
            ? formatUSPhoneNumber(answer)
            : ""}
        </Text>
      );
      break;

    case AF.EmailType:
    case AF.FreeTextType:
      children = (
        <Text color={answerColor} fontWeight={answerFontWeight}>
          {typeof answer === "string" && answer}
        </Text>
      );
      break;

    case AF.SingleSelectType:
    case AF.GradesType:
      const answerOption = question.options.find((o) => o.id === answer);
      const isAnswerOptionArchived = answerOption?.isArchived ?? false;
      const answerOptionLabel = answerOption?.label ?? "";
      children = (
        <Text
          color={answerColor}
          fontWeight={answerFontWeight}
          variant={isAnswerOptionArchived ? "placeholder" : undefined}
        >
          {isAnswerOptionArchived
            ? `${answerOptionLabel} (archived)`
            : answerOptionLabel}
        </Text>
      );
      break;

    case AF.MultiSelectType:
      const optionIds: string[] = Array.isArray(answer) && answer ? answer : [];
      children =
        optionIds.length > 0 ? (
          <UnorderedList>
            {question.options
              .filter((o) => optionIds.includes(o.id))
              .map((o) => {
                const isArchived = o.isArchived ?? false;
                const label = isArchived ? `${o.label} (archived)` : o.label;
                return (
                  <ListItem key={o.id}>
                    <Text
                      color={answerColor}
                      fontWeight={answerFontWeight}
                      variant={isArchived ? "placeholder" : undefined}
                    >
                      {label}
                    </Text>
                  </ListItem>
                );
              })}
          </UnorderedList>
        ) : null;
      break;

    case AF.FileUploadType:
      children = (
        <Flex direction="column" marginTop={2}>
          {Array.isArray(answer) &&
            answer.map((documentId) => (
              <QuestionFileItem
                key={documentId}
                documentId={documentId}
                onDeleteDocument={() => {}}
                viewMode={true}
              />
            ))}
        </Flex>
      );
      break;
    case AF.AddressType:
      if (answer === undefined) {
        children = null;
        break;
      }

      const parsedAddress = FormAddressSchema.safeParse(answer);
      if (!parsedAddress.success) {
        console.error(parsedAddress.error);
        children = null;
        break;
      }

      const {
        street_address,
        street_address_line_2,
        city,
        state,
        zip_code,
        geom,
      } = parsedAddress.data;

      children =
        street_address && city ? (
          <Flex direction={"column"} gap="1rem">
            <Flex>
              <Tooltip
                isDisabled={!isAdmin}
                hasArrow
                label={
                  geom?.coordinates
                    ? `${geom?.coordinates[1]}, ${geom?.coordinates[0]}`
                    : "No latitude/longitude coordinates found"
                }
                placement="top"
                marginBottom={"1rem"}
              >
                <Text color={answerColor} fontWeight={answerFontWeight}>
                  <span>{street_address}</span>
                  <br />
                  {street_address_line_2 && (
                    <>
                      <span>{street_address_line_2}</span>
                      <br />
                    </>
                  )}
                  <span>
                    {city}, {state} {zip_code}
                  </span>
                </Text>
              </Tooltip>
            </Flex>
            {/*
            alert is removed pending: https://app.asana.com/1/1199671080483883/project/1212913571346581/task/1213156444094889?focus=true
            !geom &&
              isAdmin &&
              !(
                question.requirement !== "Required" &&
                isAddressBlank(parsedAddress.data)
              ) && (
                <InfoAlert
                  status="error"
                  children={
                    <>
                      Invalid address. Edit to review and prevent issues with
                      school ranking, automatic tagging, and more.
                    </>
                  }
                ></InfoAlert>
              )*/}
          </Flex>
        ) : (
          <NoAnswerText
            answerColor={answerColor}
            answerFontWeight={answerFontWeight}
          />
        );
      break;
    case AF.CustomQuestionType:
      if (answer === undefined || question.nestedQuestions.length === 0) {
        children = null;
        break;
      }
      const result = CustomQuestionAnswerSchema.safeParse(answer);
      if (!result.success) {
        console.error(result.error);
        children = null;
        break;
      }
      children = (
        <CustomQuestionAnswer
          questionId={question.id}
          formId={formId}
          nestedQuestions={question.nestedQuestions}
          answer={result.data.answersByQuestionId}
          referenceId={result.data.referenceId}
        />
      );
      break;
    default:
      const _exhaustiveCheck: never = question;
      children = _exhaustiveCheck;
      break;
  }

  const answerRendered =
    isNotNull(answer) && answer !== "" ? ( // 0 is a valid answer value, so cannot just check falsy
      children
    ) : (
      <NoAnswerText
        answerColor={answerColor}
        answerFontWeight={answerFontWeight}
      />
    );

  return question.permissionLevel ? (
    <Flex direction="column" {...props}>
      <Flex gap={3} alignItems="center">
        <Text
          fontSize="sm"
          color={questionColor}
          fontWeight={questionFontWeight}
          whiteSpace="pre-wrap"
        >
          {question.question}
        </Text>
        <InfoBadge status={PERMISSION_LEVELS.admin} />
      </Flex>
      {answerRendered}
    </Flex>
  ) : (
    <Flex direction="column" {...props}>
      <Text
        fontSize="sm"
        color={questionColor}
        fontWeight={questionFontWeight}
        whiteSpace="pre-wrap"
      >
        {question.question}
      </Text>
      {answerRendered}
    </Flex>
  );
};

export const NoAnswerText: React.FC<{
  answerColor?: string;
  answerFontWeight?: string;
}> = ({ answerColor, answerFontWeight }) => (
  <Text color={answerColor} fontWeight={answerFontWeight} variant="placeholder">
    No answer
  </Text>
);

const INVALID_NUMBER_MESSAGE = "Invalid number format";
const INVALID_DATE_MESSAGE = "Invalid date format";

const AnswerWithValidationWarning: React.FC<{
  errorMessage?: string;
  children: React.ReactNode;
}> = ({ errorMessage, children }) => {
  if (!errorMessage) {
    return <>{children}</>;
  }

  return (
    <Flex align="center" gap={1}>
      <Box
        sx={{
          textDecorationLine: "underline",
          textDecorationStyle: "wavy",
          textDecorationColor: "red.500",
        }}
      >
        {children}
      </Box>
      <Tooltip label={errorMessage} hasArrow placement="top" shouldWrapChildren>
        <Icon as={RiErrorWarningLine} color="red.500" boxSize="1rem" />
      </Tooltip>
    </Flex>
  );
};
